import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AudioWaveform,
  Plus,
  Music2,
  ListMusic,
  MoreVertical,
  Edit,
  Trash2,
  Copy,
  Share2,
  Clock,
  Loader2,
  Play,
  Calendar,
  Library,
  Maximize2,
  ChevronRight
} from "lucide-react";
import { toast } from "sonner";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SetlistsPage = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  const [setlists, setSetlists] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingSetlist, setEditingSetlist] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    event_date: "",
    venue: "",
    notes: ""
  });

  const fetchSetlists = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/setlists`, {
        headers: getAuthHeaders()
      });
      setSetlists(response.data);
    } catch (error) {
      console.error("Error fetching setlists:", error);
      toast.error("Error al cargar los setlists");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchSetlists();
  }, [fetchSetlists]);

  const resetForm = () => {
    setFormData({
      name: "",
      event_date: "",
      venue: "",
      notes: ""
    });
    setEditingSetlist(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }
    
    try {
      if (editingSetlist) {
        await axios.put(`${API_URL}/setlists/${editingSetlist.id}`, formData, {
          headers: getAuthHeaders()
        });
        toast.success("Setlist actualizado");
      } else {
        const response = await axios.post(`${API_URL}/setlists`, formData, {
          headers: getAuthHeaders()
        });
        toast.success("Setlist creado");
        // Navigate to edit the new setlist
        navigate(`/setlists/${response.data.id}`);
        return;
      }
      
      setShowAddDialog(false);
      resetForm();
      fetchSetlists();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    }
  };

  const handleEdit = (setlist) => {
    setFormData({
      name: setlist.name,
      event_date: setlist.event_date || "",
      venue: setlist.venue || "",
      notes: setlist.notes || ""
    });
    setEditingSetlist(setlist);
    setShowAddDialog(true);
  };

  const handleDelete = async (setlistId) => {
    if (!window.confirm("¿Eliminar este setlist?")) return;
    
    try {
      await axios.delete(`${API_URL}/setlists/${setlistId}`, {
        headers: getAuthHeaders()
      });
      toast.success("Setlist eliminado");
      fetchSetlists();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleDuplicate = async (setlist) => {
    try {
      await axios.post(`${API_URL}/setlists/${setlist.id}/duplicate`, {}, {
        headers: getAuthHeaders()
      });
      toast.success("Setlist duplicado");
      fetchSetlists();
    } catch (error) {
      toast.error("Error al duplicar");
    }
  };

  const handleShare = () => {
    toast.info("Compartir publico aun no esta disponible. Exporta PDF para compartir la setlist por ahora.");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString("es-ES", { 
      weekday: "short",
      day: "numeric", 
      month: "short"
    });
  };

  const calculateDuration = (songs) => {
    const total = songs.reduce((acc, s) => acc + (s.duration_minutes || 4), 0);
    return Math.round(total);
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <AudioWaveform className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-white font-['Manrope']">SteelMora</span>
            </Link>
            <span className="text-gray-600">/</span>
            <span className="text-white font-medium">Setlists</span>
          </div>
          
          <div className="flex items-center gap-3">
            <Link to="/library">
              <Button variant="outline" className="border-white/10 text-gray-300 gap-2">
                <Library className="w-4 h-4" />
                Biblioteca
              </Button>
            </Link>
            <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90 btn-glow gap-2" data-testid="add-setlist-btn">
                  <Plus className="w-4 h-4" />
                  Nuevo Setlist
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#18181b] border-white/10">
                <DialogHeader>
                  <DialogTitle className="text-white font-['Manrope']">
                    {editingSetlist ? "Editar Setlist" : "Nuevo Setlist"}
                  </DialogTitle>
                  <DialogDescription className="text-gray-400">
                    Crea un setlist para tu próximo evento o ensayo
                  </DialogDescription>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Nombre del Setlist *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Ej: Domingo 26 Enero, Ensayo banda..."
                      className="bg-black/30 border-white/10 text-white"
                      data-testid="setlist-name-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-300">Fecha del evento</Label>
                      <Input
                        type="date"
                        value={formData.event_date}
                        onChange={(e) => setFormData({...formData, event_date: e.target.value})}
                        className="bg-black/30 border-white/10 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300">Lugar</Label>
                      <Input
                        value={formData.venue}
                        onChange={(e) => setFormData({...formData, venue: e.target.value})}
                        placeholder="Ej: Iglesia Central"
                        className="bg-black/30 border-white/10 text-white"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-gray-300">Notas</Label>
                    <Input
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="Notas adicionales..."
                      className="bg-black/30 border-white/10 text-white"
                    />
                  </div>
                  
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => { setShowAddDialog(false); resetForm(); }} className="border-white/10 text-gray-300">
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-primary hover:bg-primary/90">
                      {editingSetlist ? "Guardar" : "Crear Setlist"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#18181b] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <ListMusic className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white font-['JetBrains_Mono']">{setlists.length}</p>
                  <p className="text-xs text-gray-500">Setlists</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#18181b] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                  <Music2 className="w-5 h-5 text-secondary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white font-['JetBrains_Mono']">
                    {setlists.reduce((acc, s) => acc + (s.songs?.length || 0), 0)}
                  </p>
                  <p className="text-xs text-gray-500">Canciones total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#18181b] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white font-['JetBrains_Mono']">
                    {setlists.filter(s => s.event_date && new Date(s.event_date) >= new Date()).length}
                  </p>
                  <p className="text-xs text-gray-500">Próximos eventos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#18181b] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-gray-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white font-['JetBrains_Mono']">
                    {setlists.reduce((acc, s) => acc + calculateDuration(s.songs || []), 0)}m
                  </p>
                  <p className="text-xs text-gray-500">Tiempo total</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Setlists List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : setlists.length === 0 ? (
          <Card className="bg-[#18181b] border-white/10 border-dashed">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <ListMusic className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2 font-['Manrope']">
                No tienes setlists todavía
              </h3>
              <p className="text-gray-400 mb-6 max-w-sm mx-auto">
                Crea tu primer setlist para organizar las canciones de tu próximo evento
              </p>
              <Button onClick={() => setShowAddDialog(true)} className="bg-primary hover:bg-primary/90 gap-2">
                <Plus className="w-4 h-4" />
                Crear primer setlist
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {setlists.map((setlist) => (
              <Card 
                key={setlist.id} 
                className="bg-[#18181b] border-white/10 card-hover cursor-pointer group"
                onClick={() => navigate(`/setlists/${setlist.id}`)}
                data-testid={`setlist-card-${setlist.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg text-white font-['Manrope'] truncate group-hover:text-primary transition-colors">
                        {setlist.name}
                      </CardTitle>
                      {setlist.venue && (
                        <p className="text-sm text-gray-500 truncate">{setlist.venue}</p>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white -mr-2">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#18181b] border-white/10">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/stage/${setlist.id}`); }} className="text-gray-300">
                          <Maximize2 className="w-4 h-4 mr-2" />
                          Modo Escenario
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleShare(setlist); }} className="text-gray-300">
                          <Share2 className="w-4 h-4 mr-2" />
                          Compartir
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEdit(setlist); }} className="text-gray-300">
                          <Edit className="w-4 h-4 mr-2" />
                          Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDuplicate(setlist); }} className="text-gray-300">
                          <Copy className="w-4 h-4 mr-2" />
                          Duplicar
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-white/10" />
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDelete(setlist.id); }} className="text-red-400">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 mb-4">
                    {setlist.event_date && (
                      <Badge variant="outline" className="bg-accent/10 border-accent/30 text-accent">
                        <Calendar className="w-3 h-3 mr-1" />
                        {formatDate(setlist.event_date)}
                      </Badge>
                    )}
                    <span className="text-sm text-gray-500">
                      {setlist.songs?.length || 0} canciones
                    </span>
                  </div>
                  
                  {/* Song preview */}
                  <div className="space-y-1">
                    {(setlist.songs || []).slice(0, 3).map((song, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span className="text-gray-600 w-5">{i + 1}.</span>
                        <span className="text-gray-300 truncate flex-1">{song.title}</span>
                        <Badge className="bg-primary/20 text-primary border-0 text-xs font-mono">
                          {song.transposed_key || song.original_key}
                        </Badge>
                      </div>
                    ))}
                    {(setlist.songs?.length || 0) > 3 && (
                      <p className="text-xs text-gray-600 pl-7">
                        +{setlist.songs.length - 3} más
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
                    <span className="text-sm text-gray-500">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {calculateDuration(setlist.songs || [])} min
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-primary transition-colors" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default SetlistsPage;
